import { CandidateStatus } from "../../graphql/generated/graphql";
import type { StakeholderAnswer } from "../../hooks/useStakeholderAnswers";
import type { StakeholderRespondent } from "../../hooks/useStakeholderRespondents";

export interface TermSummary {
  readonly respondentCount: number;
  readonly requestedCount: number;
  readonly bookedCount: number;
  /** 依頼を送ったが、まだ枠を選んでいない回答者。未送信は追いかける対象ではないので含めない */
  readonly awaitingBookingCount: number;
  readonly completedCount: number;
  readonly answerCount: number;
  readonly importedAnswerCount: number;
}

interface StatusTally {
  unsent: number;
  awaitingBooking: number;
  booked: number;
  completed: number;
}

/**
 * ステータスを4つに振り分ける。
 *
 * switch で網羅するのは、`CandidateStatus` に値が増えたときにここで型エラーを出すため。
 * 個別に数えると、増えた値がどのカードにも出ないまま合計だけずれる。
 */
function tallyByStatus(
  respondents: readonly StakeholderRespondent[],
): StatusTally {
  const tally: StatusTally = {
    unsent: 0,
    awaitingBooking: 0,
    booked: 0,
    completed: 0,
  };
  for (const respondent of respondents) {
    switch (respondent.status) {
      case CandidateStatus.Unsent:
        tally.unsent += 1;
        break;
      case CandidateStatus.Sent:
        tally.awaitingBooking += 1;
        break;
      case CandidateStatus.Booked:
        tally.booked += 1;
        break;
      case CandidateStatus.Completed:
        tally.completed += 1;
        break;
      default: {
        const unhandled: never = respondent.status;
        throw new Error(`未対応の候補者ステータスです: ${unhandled}`);
      }
    }
  }
  return tally;
}

/** ターム全体の進み具合。40名規模では一覧を目で数えられないため、先頭にまとめて出す */
export function buildTermSummary(
  respondents: readonly StakeholderRespondent[],
  answers: readonly StakeholderAnswer[],
): TermSummary {
  const tally = tallyByStatus(respondents);

  return {
    respondentCount: respondents.length,
    requestedCount: respondents.length - tally.unsent,
    bookedCount: tally.booked,
    awaitingBookingCount: tally.awaitingBooking,
    completedCount: tally.completed,
    answerCount: answers.length,
    importedAnswerCount: answers.filter(
      (answer) => answer.importedAnswerCount > 0,
    ).length,
  };
}

export interface SettingCounts {
  readonly requestedCount: number;
  readonly importedAnswerCount: number;
}

/**
 * 設定ごとの依頼数と取り込み済みの回答数。設定が1つも使われていなくても0で返す。
 *
 * 依頼数は関係性から回答者を重複なく数える。1人の回答者が同じ設定で複数人について語るため、
 * 関係性の行数を数えると依頼の件数より多くなる。
 */
export function buildSettingCounts(
  settingIds: readonly string[],
  relations: readonly {
    stakeholderSettingId: string;
    respondentEmail: string;
  }[],
  answers: readonly StakeholderAnswer[],
): Map<string, SettingCounts> {
  const respondentsBySettingId = new Map<string, Set<string>>();
  for (const relation of relations) {
    const respondents =
      respondentsBySettingId.get(relation.stakeholderSettingId) ??
      new Set<string>();
    respondents.add(relation.respondentEmail);
    respondentsBySettingId.set(relation.stakeholderSettingId, respondents);
  }

  const importedBySettingId = new Map<string, number>();
  for (const answer of answers) {
    if (answer.importedAnswerCount === 0) continue;
    importedBySettingId.set(
      answer.stakeholderSettingId,
      (importedBySettingId.get(answer.stakeholderSettingId) ?? 0) + 1,
    );
  }

  return new Map(
    settingIds.map((settingId) => [
      settingId,
      {
        requestedCount: respondentsBySettingId.get(settingId)?.size ?? 0,
        importedAnswerCount: importedBySettingId.get(settingId) ?? 0,
      },
    ]),
  );
}
